import React, { createContext, useState, useContext, useEffect } from 'react';
import * as firebaseService from '../services/firebaseService';
import { saveLastSelectedShop, getLastSelectedShop } from '../services/shopService';
import { useAuth } from './AuthContext';

const ShopContext = createContext();

export function useShop() {
  return useContext(ShopContext);
}

export function ShopProvider({ children }) {
  const [shops, setShops] = useState([]);
  const [currentShop, setCurrentShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load saved shop data when user logs in
  useEffect(() => {
    if (user) {
      loadShops();
    } else {
      setShops([]);
      setCurrentShop(null);
      setLoading(false);
    }
  }, [user]);

  async function loadShops() {
    try {
      setLoading(true);
      const shopList = await firebaseService.getShopsByUserId(user.uid);
      setShops(shopList);
      
      if (shopList.length > 0) {
        try {
          const lastActiveShop = await getLastSelectedShop(user.uid);
          if (lastActiveShop && shopList.find(s => s.id === lastActiveShop.id)) {
            setCurrentShop(lastActiveShop);
          } else {
            // If no last active shop or it doesn't exist anymore, use first shop
            setCurrentShop(shopList[0]);
            await saveLastSelectedShop(user.uid, shopList[0]);
          }
        } catch (error) {
          console.error('Error loading last active shop:', error);
          setCurrentShop(shopList[0]); // Fallback to first shop
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading shops:', error);
      setLoading(false);
    }
  }

  // Function to set current shop and save to Firebase
  const handleSetCurrentShop = async (shop) => {
    if (!user) throw new Error('Must be logged in to set active shop');
    if (!shop) throw new Error('Shop is required');

    try {
      // Save to Firebase first
      await saveLastSelectedShop(user.uid, shop);
      // Then update local state
      setCurrentShop(shop);
    } catch (error) {
      console.error('Error setting current shop:', error);
      throw error;
    }
  };

  const value = {
    shops,
    setShops,
    currentShop,
    setCurrentShop: handleSetCurrentShop,
    loading,
    saveShop: async (shopData) => {
      const newShop = await firebaseService.saveShop(user.uid, shopData);
      setShops(prev => [...prev, newShop]);
      return newShop;
    },
    updateShop: async (shopId, shopData) => {
      const updatedShop = await firebaseService.updateShop(shopId, shopData);
      setShops(prev => prev.map(shop => 
        shop.id === shopId ? { ...shop, ...updatedShop } : shop
      ));
      // Update current shop if it's the one being edited
      if (currentShop?.id === shopId) {
        await handleSetCurrentShop({ ...currentShop, ...updatedShop });
      }
      return updatedShop;
    },
    deleteShop: async (shopId) => {
      if (!user) throw new Error('User must be logged in to delete a shop');
      await firebaseService.deleteShop(shopId, user.uid);
      setShops(prev => prev.filter(shop => shop.id !== shopId));
      // Clear current shop if it's the one being deleted
      if (currentShop?.id === shopId) {
        setCurrentShop(null);
      }
    },
    getShopMetafields: firebaseService.getShopMetafields,
    addShopMetafield: async (shopId, metafieldData) => {
      const newMetafield = await firebaseService.addShopMetafield(shopId, metafieldData);
      setShops(prev => prev.map(shop => {
        if (shop.id === shopId) {
          return {
            ...shop,
            customMetafields: [...(shop.customMetafields || []), newMetafield]
          };
        }
        return shop;
      }));
      return newMetafield;
    },
    removeShopMetafield: async (shopId, namespace, key) => {
      await firebaseService.removeShopMetafield(shopId, namespace, key);
      setShops(prev => prev.map(shop => {
        if (shop.id === shopId) {
          return {
            ...shop,
            customMetafields: (shop.customMetafields || []).filter(
              mf => !(mf.namespace === namespace && mf.key === key)
            )
          };
        }
        return shop;
      }));
    }
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
}
